import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components';
import { Column } from 'styled/alignment/Column';
import { useSearchParams, useNavigate } from 'react-router-dom';
import paths from 'routes/paths';
import breakpoints from 'theme/breakpoints';
import { useCollapse } from 'context/general/Collapse';
import eventsApi from 'api/requests/events';
import EventDetail from './detail/EventDetail';
import indexPriority from 'theme/z-index';
import { FullyCentered } from 'styled/alignment/Center';
import sizes from 'theme/sizes';
import Loader from 'components/_UI/Loader';
import colors from 'theme/colors';
import { useEventsList } from 'context/events/EventsList';
import EventForm from './form/EventForm';
import { useEvent } from 'context/events/Event';
import DeleteEvent from './delete/DeleteEvent';
import UserLocationModal from './map/UserLocationModal';
import Text from 'components/_UI/Text';
import EventCard from './card/EventCard';
import MultipleEventsList from './map/MultipleEventsList';
import { useBackButtonCollapse } from 'context/general/BackButtonCollapse';
import routeParams from 'theme/routeParams';
import { useCurrentLocation } from 'context/user/CurrentLocation';
import EventCarousel from './carrousel/EventCarousel';
import EventsNearToYouV2 from './nearToYou/EventsNearToYouV2.js';
import CarouselSkeleton from 'components/commons/skeletons/CarouselSkeleton';
import CitiesList from 'modules/home/CitiesList';
import CategoriesList from './categories/CategoriesList';
import { useEventDetailModal } from 'context/events/EventDetailModal';
import EventsFavoritedV2 from './favorited/EventsFavoritedV2';
import EventsVisitedV2 from './visited/EventsVisitedV2';
import { useAuth } from 'context/auth/Auth';
import { useEventFilter } from 'context/events/EventFilter';
import { useFavoritedEvents } from 'context/events/FavoritedEvents';

export default function EventsV2({
    filterClick,
    eventClick,
    onLoadData,
    onLoading,
    setEventClick
}) {
    const [searchParams, setSearchParams] = useSearchParams();
    const { backButtonCollapse, setBackButtonCollapse } = useBackButtonCollapse();
    const { collapse, setCollapse } = useCollapse();
    const { eventsList, setEventsList } = useEventsList();
    const { event } = useEvent();
    const {eventDetailModal, setEventDetailModal} = useEventDetailModal();
    const { isAuth } = useAuth();
    const { eventFilter } = useEventFilter();
    const isFirstFilterRender = useRef(true);

    const [eventData, setEventData] = useState();
    const [filterData, setFilterData] = useState(null)
    const [slug, setSlug] = useState(null);
    const [modalDelete, setModalDelete] = useState(false);
    const { favoritedEvents } = useFavoritedEvents();
    const [initialLoadEmpty, setInitialLoadEmpty] = useState(false);
    const [showVisited, setShowVisited] = useState(true);
    const [activeLocations, setActiveLocations] = useState([]);
    const [locationsResolved, setLocationsResolved] = useState(false);
    const [featured, setFeatured] = useState({ status: 'loading', items: [], scope: null, city: null, state: null });
    const { currentLocation, setCurrentLocation } = useCurrentLocation();
    const navigate = useNavigate();

    useEffect(() => {
        eventsApi.getActiveLocations()
            .then(({ data }) => setActiveLocations(data?.states || []))
            .catch(() => {})
            .finally(() => setLocationsResolved(true));
    }, []);

    function getStateForCity(cityName) {
        for (const state of activeLocations) {
            if (state.cities?.some(c => c.name === cityName)) return state.code;
        }
        return '';
    }

    /**
     * Destaques da cidade. O carrossel deixou de ser uma fatia da lista de baixo
     * (`slice(0, 12)` mostrava exatamente os mesmos eventos duas vezes) e passou a
     * ter fonte própria, que ordena por disponibilidade real de lote.
     */
    useEffect(() => {
        // O uf é parte da chave do pedido: sem ele o backend não tem o degrau
        // intermediário do fallback e pula direto de cidade para plataforma.
        // Quando a localização veio do GPS ela traz só a cidade, então esperamos
        // a lista de locais resolver antes de pedir — assim não disparamos dois
        // requests nem trocamos o título da seção com a página já montada.
        if (currentLocation?.city && !currentLocation?.state && !locationsResolved) {
            return;
        }

        let cancelled = false;

        async function getFeatured() {
            setFeatured(prev => ({ ...prev, status: 'loading' }));

            try {
                const params = {};

                if (currentLocation?.city) {
                    params.city = currentLocation.city;
                }

                const stateCode = currentLocation?.state || getStateForCity(currentLocation?.city);
                if (stateCode) {
                    params.state = stateCode;
                }

                const { data } = await eventsApi.featured(params);

                if (cancelled) return;

                setFeatured({
                    status: 'ready',
                    items: data?.data || [],
                    scope: data?.meta?.scope || null,
                    city: data?.meta?.city || null,
                    state: data?.meta?.state || null,
                });
            } catch (error) {
                // A faixa não é essencial para a página: em erro ela some e o
                // resto continua funcionando, sem estado de erro próprio.
                if (!cancelled) {
                    setFeatured({ status: 'ready', items: [], scope: null, city: null, state: null });
                }
            }
        }

        getFeatured();

        return () => { cancelled = true; };
    }, [currentLocation?.city, currentLocation?.state, locationsResolved]);

    /**
     * O título vem do alcance que o backend acabou usando. Com um título fixo,
     * quem está em Pelotas e recebe eventos de São Paulo (porque a cidade não
     * tinha destaques suficientes) lê a faixa como defeito.
     */
    function featuredTitle() {
        if (featured.scope === 'city' && featured.city) {
            return `Eventos em destaque em ${featured.city}`;
        }

        if (featured.scope === 'state' && featured.state) {
            return `Eventos em destaque em ${featured.state}`;
        }

        if (featured.scope === 'platform') {
            return 'Eventos em destaque no Brasil';
        }

        return 'Eventos em destaque';
    }

    useEffect(() => {
        async function run() {
            await getEvents();
            await getEventByRouteParam();
        }
        run();
    }, [currentLocation?.city])

    useEffect(() => {
        if (isFirstFilterRender.current) {
            isFirstFilterRender.current = false;
            return;
        }
        async function run() {
            if (eventFilter) {
                await getFilteredEvents(eventFilter);
            } else {
                await getEvents();
            }
        }
        run();
    }, [eventFilter])

    useEffect(() => {
        async function run() {
            if (eventClick) {
                onEventClick(eventClick)
            }
        }
        run();
    }, [eventClick]);

    async function getEventByRouteParam() {
        const eventSlug = searchParams.get('evento');

        const latParam = searchParams.get('lat')
        const longParam = searchParams.get('long')

        const term = searchParams.get('term')
        const filter_date = searchParams.get('filter_date')
        const classification_id = searchParams.get('classification_id')
        const tags = searchParams.get('tags')
        const distance_range = searchParams.get('distance_range')
        const min_date = searchParams.get('min_date')
        const max_date = searchParams.get('max_date')
        const city = searchParams.get('city')

        if (term || filter_date || classification_id || tags || distance_range || (min_date && max_date) || city) {
            setFilterData({ term, filter_date, classification_id, tags, distance_range, min_date, max_date, city })
            return
        }

        if (latParam && longParam) {
            renderMultipleEvents(latParam, longParam)
            return
        }

        if (!eventSlug) {
            return
        }

        setCollapse({
            ...collapse,
            withContent: true,
            height: 'max',
            arrowPosition: false,
            componentToRender: () => (
                <FullyCentered height='100%'>
                    <Loader />
                </FullyCentered>
            )
        })

        async function renderMultipleEvents(data) {
            // setCollapse({
            //     ...collapse,
            //     withContent: true,
            //     height: 'max',
            //     arrowPosition: false,
            //     componentToRender: () => data && <MultipleEventsList data={data} />
            // })
        }

        try {
            const { data } = await eventsApi.getBySlug(eventSlug);
            setSlug(eventSlug);
            setCollapse({
                ...collapse,
                withContent: true,
                height: 'max',
                arrowPosition: false,
                componentToRender: () => data && <EventDetail data={data.data} />
            })
        } catch (error) {
            setCollapse({
                ...collapse,
                withContent: true,
                height: 'max',
                arrowPosition: false,
                componentToRender: () => (
                    <FullyCentered height='100%'>
                        <Text>Ocorreu um erro, tente novamente mais tarde.</Text>
                    </FullyCentered>
                )
            })
        }
    }

    async function getEvents() {
        onLoading(true)
        try {
            const { data } = await eventsApi.filter({
                grouped: true,
                limit: 500,
            });
            const arraysWithMultiple = data?.data?.filter(obj => obj.hasOwnProperty('multiples'));
            const arraysWithoutMultiple = data?.data?.filter(obj => !obj.hasOwnProperty('multiples'));

            const formatedData = {
                data: arraysWithoutMultiple,
                withMultiples: arraysWithMultiple
            }

            onLoadData(formatedData);
            setEventData(formatedData);
            setEventsList(formatedData);
        } catch (error) {
            console.log('error G-E')
            setEventData({ data: [], withMultiples: [] });
        } finally {
            onLoading(false)
        }
    }

    async function getFilteredEvents(filter) {
        onLoading(true);
        try {
            const params = {
                city: currentLocation?.city,
                state: currentLocation?.state,
                grouped: true,
                limit: 500,
                ...filter,
                categories: filter?.categories?.length ? filter.categories.join(',') : undefined,
            };
            const { data } = await eventsApi.filter(params);
            const arraysWithMultiple = data?.data?.filter(obj => obj.hasOwnProperty('multiples'));
            const arraysWithoutMultiple = data?.data?.filter(obj => !obj.hasOwnProperty('multiples'));

            const formatedData = {
                data: arraysWithoutMultiple,
                withMultiples: arraysWithMultiple
            };

            onLoadData(formatedData);
            setEventData(formatedData);
            setEventsList(formatedData);
        } catch (error) {
            console.log('error G-F-E');
            setEventData({ data: [], withMultiples: [] });
        } finally {
            onLoading(false);
        }
    }

    return (
        <>
            <EventsContainer>
                {featured.status === 'loading' ? (
                    <Column>
                        <SectionTitle>Eventos em destaque</SectionTitle>
                        <CarouselSkeleton />
                    </Column>
                ) : featured.items.length > 0 ? (
                    <Column>
                        <SectionTitle>{featuredTitle()}</SectionTitle>
                        <EventCarousel data={featured.items} />
                    </Column>
                ) : null}

                <Column marginTop='48px'>
                    <SectionHeader>
                        <SectionTitle>Eventos próximos a você</SectionTitle>
                        <VerTodosLink onClick={() => {
                            if (currentLocation?.city) {
                                const stateCode = currentLocation.state || getStateForCity(currentLocation.city);
                                const citySlug = [currentLocation.city, stateCode]
                                    .filter(Boolean).join('-').toLowerCase().replace(/\s+/g, '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                                navigate(`${paths.events.search}?city=${encodeURIComponent(citySlug)}`);
                            } else {
                                navigate(paths.events.search);
                            }
                        }}>
                            Ver todos →
                        </VerTodosLink>
                    </SectionHeader>
                    <EventsNearToYouV2 onEventClick={e => setEventDetailModal(e)} />
                </Column>

                {isAuth && (!initialLoadEmpty || favoritedEvents.length > 0) && (
                    <Column marginTop='48px'>
                        <SectionTitle>Eventos favoritos</SectionTitle>
                        <EventsFavoritedV2
                            onEventClick={e => setEventDetailModal(e)}
                            onLoadEmpty={() => setInitialLoadEmpty(true)}
                        />
                    </Column>
                )}

                {isAuth && showVisited && (
                    <Column marginTop='48px'>
                        <SectionTitle>Eventos visitados</SectionTitle>
                        <EventsVisitedV2
                            onEventClick={e => setEventDetailModal(e)}
                            onLoadEmpty={() => setShowVisited(false)}
                        />
                    </Column>
                )}

                <Column marginTop='48px'>
                    <SectionTitle>Categorias</SectionTitle>
                    <CategoriesList />
                </Column>

                <Column marginTop='48px'>
                    <SectionTitle>O que fazer por aí?</SectionTitle>
                    <CitiesList />
                </Column>
            </EventsContainer>

        </>
    )
}

const EventsContainer = styled(Column)`
    width: 100%;
    height: auto;
    margin-top: 24px;

    @media screen and (min-width: ${breakpoints.lg}) {
        width: 100%;
        display: flex;
        margin-top: 20px;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 10px;

    /* O título traz margin-bottom próprio, que aqui somava à do cabeçalho e
       abria um vão entre "Eventos próximos a você" e a linha de resultados. */
    & > h2 {
        margin-bottom: 0;
    }
`;

const SectionTitle = styled.h2`
    font-size: 20px;
    font-weight: 700;
    color: ${colors.nightDark};
    margin: 0 0 12px 0;
`;

const VerTodosLink = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.occasionalPurple};
    cursor: pointer;
    white-space: nowrap;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.75;
    }
`;
